/**
 * Typed client over the `contacts` REST surface (PR-B).
 *
 * Per FD § 1.4. The surface is intentionally NOT 1:1 with PR-B endpoints:
 *
 *  - `bulkImport` is NOT exposed (UI parked — FD § 10).
 *  - `issueToken` is NOT exposed (NFR-SEC-15 / NFR-SEC-8: `POST /tokens`
 *    is API-key-only on the server; cookie-auth callers get 401. The
 *    dashboard MUST NOT mint contact JWTs.).
 *
 * Path conventions — locked by PR-B live-test deltas:
 *  - Slashed action paths: `POST /contacts/bulk-import` (we don't use it
 *    here) and `POST /signing-keys/rotate` — NOT colons.
 *  - All paths relative to `/api/workspaces/:wsid/...` — the workspace
 *    UUID is bound on the `WorkspaceFetcher` at construction.
 */

import type {
  ContactDto,
  MemoryDto,
  MemorySettingsDto,
  PaginatedDto,
  RotateSigningKeyResponseDto,
  RunSummaryDto,
  SessionSummaryDto,
  SigningKeyDto,
  SoftDeleteResponseDto,
} from "./dtos";
import {
  mapContactDto,
  mapMemoryDto,
  mapMemorySettingsDto,
  mapPaginated,
  mapRotateSigningKeyResponse,
  mapRunSummaryDto,
  mapSessionSummaryDto,
  mapSigningKeyDto,
  mapSoftDeleteDto,
  profileToSnake,
} from "./mappers";
import type {
  Contact,
  ContactProfilePatch,
  CreateContactInput,
  ListContactsFilter,
  Memory,
  MemorySettings,
  MemorySettingsPatch,
  PageInput,
  Paginated,
  RotateSigningKeyResult,
  RunSummary,
  SessionSummary,
  SigningKey,
  SigningKeyAlgorithm,
  SoftDeleteResult,
} from "./types";
import type { WorkspaceFetcher } from "./workspace-fetcher";

export class ContactsClient {
  constructor(private readonly fetcher: WorkspaceFetcher) {}

  // ── Contacts CRUD ─────────────────────────────────────────────

  async list(filter: ListContactsFilter = {}): Promise<Paginated<Contact>> {
    const dto = await this.fetcher.get<PaginatedDto<ContactDto>>("/contacts", {
      status: filter.status,
      search: filter.search,
      cursor: filter.cursor,
      limit: filter.limit,
      sort: filter.sort,
    });
    return mapPaginated(dto, mapContactDto);
  }

  async create(input: CreateContactInput): Promise<Contact> {
    const body: Record<string, unknown> = {
      user_id: input.userId,
      ...profileToSnake(input),
    };
    const dto = await this.fetcher.post<ContactDto>("/contacts", body);
    return mapContactDto(dto);
  }

  async get(contactId: string): Promise<Contact> {
    const dto = await this.fetcher.get<ContactDto>(`/contacts/${contactId}`);
    return mapContactDto(dto);
  }

  async update(contactId: string, patch: ContactProfilePatch): Promise<Contact> {
    const dto = await this.fetcher.patch<ContactDto>(
      `/contacts/${contactId}`,
      profileToSnake(patch),
    );
    return mapContactDto(dto);
  }

  async disable(contactId: string, reason?: string): Promise<Contact> {
    const body = reason !== undefined ? { reason } : {};
    const dto = await this.fetcher.post<ContactDto>(`/contacts/${contactId}/disable`, body);
    return mapContactDto(dto);
  }

  async enable(contactId: string): Promise<Contact> {
    const dto = await this.fetcher.post<ContactDto>(`/contacts/${contactId}/enable`, {});
    return mapContactDto(dto);
  }

  async softDelete(contactId: string): Promise<SoftDeleteResult> {
    const dto = await this.fetcher.delete<SoftDeleteResponseDto>(`/contacts/${contactId}`);
    return mapSoftDeleteDto(dto);
  }

  async sessions(contactId: string, page: PageInput = {}): Promise<Paginated<SessionSummary>> {
    const dto = await this.fetcher.get<PaginatedDto<SessionSummaryDto>>(
      `/contacts/${contactId}/sessions`,
      { cursor: page.cursor, limit: page.limit },
    );
    return mapPaginated(dto, mapSessionSummaryDto);
  }

  async runs(contactId: string, page: PageInput = {}): Promise<Paginated<RunSummary>> {
    const dto = await this.fetcher.get<PaginatedDto<RunSummaryDto>>(`/contacts/${contactId}/runs`, {
      cursor: page.cursor,
      limit: page.limit,
    });
    return mapPaginated(dto, mapRunSummaryDto);
  }

  // ── Long-term memory (PR 3.5) ─────────────────────────────────

  async listMemories(contactId: string): Promise<Memory[]> {
    const dtos = await this.fetcher.get<MemoryDto[]>(`/contacts/${contactId}/memories`);
    return dtos.map(mapMemoryDto);
  }

  async updateMemory(
    memoryId: string,
    patch: { stage?: "temporary" | "permanent"; visibility?: "private" | "workspace" },
  ): Promise<Memory> {
    const dto = await this.fetcher.patch<MemoryDto>(`/memories/${memoryId}`, patch);
    return mapMemoryDto(dto);
  }

  async deleteMemory(memoryId: string): Promise<SoftDeleteResult> {
    const dto = await this.fetcher.delete<SoftDeleteResponseDto>(`/memories/${memoryId}`);
    return mapSoftDeleteDto(dto);
  }

  async getMemorySettings(): Promise<MemorySettings> {
    const dto = await this.fetcher.get<MemorySettingsDto>("/memory-settings");
    return mapMemorySettingsDto(dto);
  }

  async updateMemorySettings(patch: MemorySettingsPatch): Promise<MemorySettings> {
    const body: Record<string, unknown> = {};
    if (patch.memoryEnabled !== undefined) body["memory_enabled"] = patch.memoryEnabled;
    if (patch.memoryRecallTopK !== undefined) body["memory_recall_top_k"] = patch.memoryRecallTopK;
    if (patch.memoryRetentionDays !== undefined)
      body["memory_retention_days"] = patch.memoryRetentionDays;
    const dto = await this.fetcher.patch<MemorySettingsDto>("/memory-settings", body);
    return mapMemorySettingsDto(dto);
  }

  // ── Signing keys (admin-only on the server) ───────────────────

  async listSigningKeys(): Promise<SigningKey[]> {
    const dtos = await this.fetcher.get<SigningKeyDto[]>("/signing-keys");
    return dtos.map(mapSigningKeyDto);
  }

  async rotateSigningKey(algorithm?: SigningKeyAlgorithm): Promise<RotateSigningKeyResult> {
    const body = algorithm !== undefined ? { algorithm } : {};
    // FD delta #1 — slashed action path, NOT colon.
    const dto = await this.fetcher.post<RotateSigningKeyResponseDto>("/signing-keys/rotate", body);
    return mapRotateSigningKeyResponse(dto);
  }

  async retireSigningKey(kid: string): Promise<void> {
    await this.fetcher.delete<{ kid: string; status: "retired" } | undefined>(
      `/signing-keys/${encodeURIComponent(kid)}`,
    );
  }
}
